import { supabase } from '../supabase';

// Error handling helper
export class DatabaseError extends Error {
  constructor(message: string, public originalError?: unknown) {
    super(message);
    this.name = 'DatabaseError';
  }
}

export const handleError = (error: unknown, context: string) => {
  console.error(`Database error in ${context}:`, error);
  const message = error instanceof Error ? error.message : String(error);
  throw new DatabaseError(`Failed to ${context}: ${message}`, error);
};

/**
 * Check if current user has access to workspace
 * Superadmins have access to all workspaces
 */
export const checkWorkspaceAccess = async (workspaceId: string): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Superadmins have access to all workspaces
    const isSuperadmin = user.app_metadata?.is_superadmin === true;
    if (isSuperadmin) {
      return true;
    }

    const { data, error } = await supabase
      .from('workspace_users')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('user_id', user.id)
      .single();

    return !error && !!data;
  } catch (error) {
    console.error('Error checking workspace access:', error);
    return false;
  }
};

/**
 * Check if current user has admin access to workspace
 * Superadmins always have admin access (they can bypass at database level)
 * Frontend enforces assumed role restrictions for UI purposes
 */
export const checkWorkspaceAdmin = async (workspaceId: string): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Superadmins always have admin access at database level
    const isSuperadmin = user.app_metadata?.is_superadmin === true;
    if (isSuperadmin) {
      return true;
    }

    const { data, error } = await supabase
      .from('workspace_users')
      .select('role')
      .eq('workspace_id', workspaceId)
      .eq('user_id', user.id)
      .eq('role', 'admin')
      .single();

    return !error && !!data;
  } catch (error) {
    console.error('Error checking workspace admin status:', error);
    return false;
  }
};
